/**
 * What every developer's agents are doing right now — ticketed or not.
 *
 * The rest of the product is organised around tasks. The bug hunt follows bugs, specification
 * progress follows documents, reports group by task type. None of them can see work that never had
 * a ticket: configuring an integration, investigating a question, pairing on a design. That is real
 * engineering time, and an organization measuring only ticketed work concludes the rest is not
 * happening.
 *
 * This is the same idea as the specification screen, applied to everything outside its scope.
 *
 * It is deliberately not a productivity view. Developers are ordered by whether an agent is
 * currently reporting and then alphabetically — never by output — and there is no score, no
 * ranking, and no column anybody can sort to rank people against each other. §43 rules out the
 * feature this screen most resembles, and the difference is that these figures describe agents.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Bot, ChevronDown, ChevronRight, Compass, Ticket } from 'lucide-react'
import { api } from '../lib/api'
import { useI18n, type Locale } from '../lib/i18n'
import { formatCost, formatDuration, formatNumber, formatRelative } from '../lib/format'
import { Avatar, Badge, EmptyState, Panel, ProgressBar, Skeleton, toneForState } from '../components/primitives'
import type { ActivitySession, DeveloperActivity } from '../lib/types'

const KINDS = [
  { value: '', key: 'common.all' },
  { value: 'Exploration', key: 'activity.exploration' },
  { value: 'TaskWork', key: 'activity.taskWork' },
] as const

export default function Activity() {
  const { t, locale } = useI18n()
  const [kind, setKind] = useState<string>('')
  const [includeSimulated, setIncludeSimulated] = useState(true)
  const [expanded, setExpanded] = useState<string | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['activity', { kind, includeSimulated }],
    queryFn: () => api.activity.get({ kind: kind || undefined, includeSimulated }),
    // Live enough to be useful, rare enough not to hammer an endpoint that aggregates usage.
    refetchInterval: 10_000,
  })

  const developers = data ?? []
  const working = developers.filter((d) => d.working).length

  return (
    <div className="stack stack--loose">
      <div className="row row--wrap">
        <div className="segmented">
          {KINDS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`segmented__item${kind === option.value ? ' segmented__item--active' : ''}`}
              onClick={() => setKind(option.value)}
            >
              {t(option.key)}
            </button>
          ))}
        </div>

        <label className="row row--tight">
          <input
            type="checkbox"
            checked={includeSimulated}
            onChange={(event) => setIncludeSimulated(event.target.checked)}
          />
          <span className="small">{t('reports.includeSimulated')}</span>
        </label>

        <div className="spacer" />

        <span className="tiny muted">{t('activity.workingNow', { count: working })}</span>
      </div>

      {/*
        Said before any number is read. These figures describe agents, and the screen that shows
        per-person totals is one sentence away from the ranking §43 rules out.
      */}
      <p className="tiny muted">{t('activity.note')}</p>

      {isLoading ? (
        <div className="stack">
          <Skeleton height={76} />
          <Skeleton height={76} />
        </div>
      ) : developers.length === 0 ? (
        <Panel>
          <EmptyState title={t('activity.empty')} body={t('activity.emptyHint')} />
        </Panel>
      ) : (
        <div className="stack stack--tight">
          {developers.map((developer) => (
            <DeveloperCard
              key={developer.developerId}
              developer={developer}
              open={expanded === developer.developerId}
              onToggle={() =>
                setExpanded(expanded === developer.developerId ? null : developer.developerId)
              }
              locale={locale}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function DeveloperCard({
  developer,
  open,
  onToggle,
  locale,
}: {
  developer: DeveloperActivity
  open: boolean
  onToggle: () => void
  locale: Locale
}) {
  const { t } = useI18n()

  return (
    <Panel flush>
      <button type="button" className="activity__head" onClick={onToggle} aria-expanded={open}>
        {open ? <ChevronDown size={15} className="muted" /> : <ChevronRight size={15} className="muted" />}

        <Avatar name={developer.displayName} color={developer.avatarColor} />

        <div className="stack" style={{ gap: 0, minWidth: 0, flex: 1, textAlign: 'left' }}>
          <span className="bold truncate">{developer.displayName}</span>
          <span className="tiny muted truncate">
            {developer.teamName ?? t('common.unassigned')}
          </span>
        </div>

        {/*
          "Working" means an agent reported recently. Never "active" or "idle" about the person —
          somebody may be reading, thinking, in a meeting, or working without an agent at all.
        */}
        <Badge tone={developer.working ? 'ok' : 'neutral'}>
          {developer.working ? t('activity.agentRunning') : t('activity.noAgent')}
        </Badge>

        <div className="activity__totals">
          <span className="tiny muted">{t('activity.tokensToday')}</span>
          <span className="mono small">{formatNumber(developer.tokensToday, locale)}</span>
        </div>

        <div className="activity__totals">
          <span className="tiny muted">{t('live.cost')}</span>
          <span className="mono small">
            {developer.costAccuracy === 'Unavailable' || developer.costToday === null
              ? '—'
              : formatCost(developer.costToday, locale)}
          </span>
        </div>
      </button>

      {open && (
        <div className="activity__sessions">
          <Link to={`/developers/${developer.developerId}/day`} className="btn btn--sm" style={{ alignSelf: 'flex-start' }}>
            {t('day.open')}
          </Link>
          {developer.sessions.length === 0 ? (
            <span className="tiny muted">{t('bugHunt.noActiveSession')}</span>
          ) : (
            developer.sessions.map((session) => (
              <SessionRow key={session.workSessionId} session={session} locale={locale} />
            ))
          )}
        </div>
      )}
    </Panel>
  )
}

function SessionRow({ session, locale }: { session: ActivitySession; locale: Locale }) {
  const { t } = useI18n()
  const exploration = session.kind === 'Exploration'

  return (
    <Link to={`/work/${session.workSessionId}`} className="activity__session">
      <div className="row row--tight">
        {/* The kind, in an icon and in words. A compass is not self-explanatory on its own. */}
        {exploration ? <Compass size={14} className="muted" /> : <Ticket size={14} className="muted" />}
        <Badge tone={exploration ? 'info' : 'neutral'}>
          {exploration ? t('activity.exploration') : t('activity.taskWork')}
        </Badge>

        {session.taskExternalId && <span className="mono tiny muted">{session.taskExternalId}</span>}

        <div className="spacer" />

        <Badge tone={toneForState(session.activityState)}>{t(`state.${session.activityState}`)}</Badge>
      </div>

      <span className="small truncate">{session.subject ?? t('activity.noSubject')}</span>

      {/*
        Progress only where it means something. An exploration session has no plan, so it has no
        computed percentage — and a bar sitting at zero would read as "no progress" rather than
        "not a measure that applies here".
      */}
      {!exploration && <ProgressBar value={session.progressPercent} showValue size="sm" />}

      {(session.currentStepNote || session.currentStepTitle) && (
        <div className="row row--tight tiny muted">
          <Bot size={12} />
          <span className="truncate">{session.currentStepNote ?? session.currentStepTitle}</span>
        </div>
      )}

      <div className="row row--between tiny muted">
        <span>
          {session.providerName}
          {session.modelName ? ` · ${session.modelName}` : ''}
        </span>
        <span>{formatDuration(session.durationSeconds, locale)}</span>
      </div>

      <div className="row row--between tiny muted">
        <span className="mono">
          {formatNumber(session.inputTokens + session.outputTokens, locale)} {t('activity.tokens')}
          {/* The cached figure is a subset of the total, so it reads as a qualifier rather than a
              second count — and never repeats the word. */}
          {session.cachedInputTokens > 0 &&
            ` (${formatNumber(session.cachedInputTokens, locale)} ${t('activity.cached')})`}
        </span>
        <span>
          {session.costAccuracy === 'Unavailable' || session.costUsd === null
            ? t('cost.Unavailable')
            : `${formatCost(session.costUsd, locale)} · ${t(`cost.${session.costAccuracy}`)}`}
        </span>
      </div>

      <span className="tiny muted">
        {t('activity.lastEvent')}: {formatRelative(session.lastActivityAt, locale)}
      </span>
    </Link>
  )
}
